import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../components/AuthContext';
import { homePathFor } from '../utils/permissions';
import SEO from '../components/SEO';
import Logo from '../components/Logo';
import HeroSection from '../components/landing/HeroSection';
import AIFeaturesSection from '../components/landing/AIFeaturesSection';
import FeaturesSection from '../components/landing/FeaturesSection';
import PricingSection from '../components/landing/PricingSection';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import ContactSection from '../components/landing/ContactSection';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  // Eğer kullanıcı giriş yapmışsa dashboard'a yönlendir
  React.useEffect(() => {
    if (user) {
      navigate(homePathFor(user));
    }
  }, [user, navigate]);

  return (
    <>
      <SEO
        title={t('landing.seo.homeTitle')}
        description={t('landing.seo.homeDescription')}
        keywords={t('landing.seo.homeKeywords')}
        canonical="https://www.runvya.com"
      />
      <div className="min-h-screen" itemScope itemType="https://schema.org/WebPage">
        {/* Navigation Bar */}
      <header>
      <nav className="bg-white dark:bg-gray-800 shadow-md fixed w-full top-0 z-50" aria-label={t('landing.page.navAriaLabel')}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Logo />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#ai-features" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 font-medium flex items-center">
                🤖 {t('landing.nav.aiFeatures')}
              </a>
              <a href="#features" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 font-medium">{t('landing.nav.modules')}</a>
              <a href="#pricing" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 font-medium">{t('landing.nav.pricing')}</a>
              <a href="#contact" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 font-medium">{t('landing.nav.contact')}</a>
              <a href="/partner" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 font-medium">🤝 {t('landing.page.partnerLink')}</a>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher variant="landing" />
              <button
                onClick={() => navigate('/login')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition duration-300"
                data-testid="nav-login-button"
              >
                {t('landing.nav.login')}
              </button>
            </div>
          </div>
        </div>
      </nav>
      </header>

      {/* Main Content */}
      <main className="pt-16" role="main">
        <HeroSection />
        <AIFeaturesSection />
        <FeaturesSection />
        <PricingSection />
        <TestimonialsSection />
        <ContactSection />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Logo onDark />
              </div>
              <h3 className="text-white font-semibold mb-2">Run + Via</h3>
              <p className="text-gray-400 mb-2 text-sm italic">
                {t('landing.footer.slogan')}
              </p>
              <p className="text-gray-400 mb-4 text-sm">
                <span className="font-semibold text-gray-300">R</span>eliable <span className="font-semibold text-gray-300">U</span>nified <span className="font-semibold text-gray-300">N</span>etwork for<br/>
                <span className="font-semibold text-gray-300">V</span>alue <span className="font-semibold text-gray-300">Y</span>ielding <span className="font-semibold text-gray-300">A</span>utomations
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="bg-purple-600 text-white px-3 py-1 rounded text-xs font-semibold">🤖 {t('landing.footer.badges.aiPowered')}</span>
                <span className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold">{t('landing.footer.badges.multiTenant')}</span>
                <span className="bg-green-600 text-white px-3 py-1 rounded text-xs font-semibold">{t('landing.footer.badges.modules')}</span>
                <span className="bg-yellow-600 text-white px-3 py-1 rounded text-xs font-semibold">{t('landing.footer.badges.multiCurrency')}</span>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.sections.modules')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white">🤝 CRM</a></li>
                <li><a href="#features" className="hover:text-white">✅ {t('shared.moduleNames.tasks')}</a></li>
                <li><a href="#features" className="hover:text-white">💰 {t('shared.moduleNames.sales')}</a></li>
                <li><a href="#features" className="hover:text-white">📦 {t('shared.moduleNames.inventory')}</a></li>
                <li><a href="#features" className="hover:text-white">📊 {t('shared.moduleNames.accounting')}</a></li>
                <li><a href="#features" className="hover:text-white">💳 {t('shared.moduleNames.finance')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.sections.more')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white">🏭 {t('shared.moduleNames.production')}</a></li>
                <li><a href="#features" className="hover:text-white">👥 {t('shared.moduleNames.hr')}</a></li>
                <li><a href="#features" className="hover:text-white">🔧 {t('shared.moduleNames.service')}</a></li>
                <li><a href="#features" className="hover:text-white">⚖️ {t('shared.moduleNames.tender')}</a></li>
                <li><a href="#features" className="hover:text-white">🛒 {t('shared.moduleNames.purchasing')}</a></li>
                <li><a href="#features" className="hover:text-white">💼 {t('shared.moduleNames.assets')}</a></li>
                <li><a href="#features" className="hover:text-white">🏢 {t('shared.moduleNames.office')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('landing.footer.sections.company')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white">{t('landing.footer.links.features')}</a></li>
                <li><a href="#pricing" className="hover:text-white">{t('landing.footer.links.pricing')}</a></li>
                <li><a href="#contact" className="hover:text-white">{t('landing.footer.links.contact')}</a></li>
                <li><a href="/partner" className="hover:text-white">{t('landing.page.footerPartnerLink')}</a></li>
                <li><a href="/login" className="hover:text-white">{t('landing.footer.links.login')}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm text-center md:text-left mb-4 md:mb-0">
                &copy; 2025-{new Date().getFullYear()} Runvya ERP. {t('landing.footer.copyright')} | {t('landing.footer.madeWith')}
              </p>
              <div className="flex space-x-6 text-sm text-gray-400">
                <a href="#" className="hover:text-white">{t('landing.footer.legal.privacy')}</a>
                <a href="#" className="hover:text-white">{t('landing.footer.legal.terms')}</a>
                <a href="#" className="hover:text-white">{t('landing.footer.legal.kvkk')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
